import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DivisionToggle from './DivisionToggle';

const ADMIN_NAV: { path: string; icon: string; label: string; exact?: boolean }[] = [
  { path: '/',                icon: '🏠', label: 'Dashboard',       exact: true },
  { path: '/pipeline',        icon: '📊', label: 'Pipeline' },
  { path: '/leads',           icon: '📍', label: 'Lead Center' },
  { path: '/contacts',        icon: '👤', label: 'Contacts' },
  { path: '/estimates',       icon: '📋', label: 'Estimates' },
  { path: '/tasks',           icon: '✅', label: 'Tasks' },
  { path: '/calendar',        icon: '🗓️', label: 'Calendar' },
  { path: '/financials',      icon: '💵', label: 'Financials' },
  { path: '/commissions',     icon: '💰', label: 'Commissions' },
  { path: '/leaderboard',     icon: '🏆', label: 'Leaderboard' },
  { path: '/team',            icon: '👥', label: 'Team' },
  { path: '/employees',       icon: '📁', label: 'Employees' },
  { path: '/settings',        icon: '⚙️', label: 'Integrations' },

];

const SPECIALIST_NAV: { path: string; icon: string; label: string; exact?: boolean }[] = [
  { path: '/sales-manager',     icon: '🏠', label: 'Dashboard' },
  { path: '/pipeline',          icon: '🏠', label: 'Roof Pipeline' },
  { path: '/tasks',             icon: '✅', label: 'Tasks' },
  { path: '/training-tracker',  icon: '📅', label: 'Training Tracker' },
  { path: '/hiring-pipeline',   icon: '💼', label: 'Hiring Pipeline' },
  { path: '/team-activity',     icon: '📍', label: 'Team Activity' },
  { path: '/employees',         icon: '📁', label: 'Onboarding' },
  { path: '/leaderboard',       icon: '🏆', label: 'Leaderboard' },
  { path: '/handbook',          icon: '📖', label: 'Handbook' },
];

// Government Contracting V3 nav — three pipelines + workspace pages (V3.3)
const GOV_NAV: { path: string; icon: string; label: string; exact?: boolean }[] = [
  { path: '/gov',                icon: '🎯', label: 'Dashboard',       exact: true },
  { path: '/gov/solicitations',  icon: '📑', label: 'Solicitations' },
  { path: '/gov/construction',   icon: '🏗️', label: 'Construction' },
  { path: '/gov/tasks',          icon: '📋', label: 'Tasks' },
  { path: '/gov/calendar',       icon: '🗓️', label: 'Calendar' },
  { path: '/gov/contacts',       icon: '👥', label: 'Contacts' },
  { path: '/gov/documents',      icon: '📎', label: 'Documents' },
  { path: '/gov/financials',     icon: '💰', label: 'Financials' },
];

const CO_NAV: { path: string; icon: string; label: string; exact?: boolean }[] = [
  { path: '/gov',                icon: '🎯', label: 'Dashboard',       exact: true },
  { path: '/gov/solicitations',  icon: '📑', label: 'Solicitations' },
  { path: '/gov/construction',   icon: '🏗️', label: 'Construction' },
  { path: '/gov/tasks',          icon: '📋', label: 'Tasks' },
  { path: '/gov/calendar',       icon: '🗓️', label: 'Calendar' },
  { path: '/gov/contacts',       icon: '👥', label: 'Contacts' },
  { path: '/gov/documents',      icon: '📎', label: 'Documents' },
  { path: '/gov/financials',     icon: '💰', label: 'Financials' },
];

// Production Manager — pipeline-focused view
const PM_NAV: { path: string; icon: string; label: string; exact?: boolean }[] = [
  { path: '/',           icon: '🏠', label: 'Dashboard', exact: true },
  { path: '/pipeline',   icon: '📊', label: 'Pipeline' },
  { path: '/tasks',      icon: '✅', label: 'Tasks' },
  { path: '/calendar',   icon: '🗓️', label: 'Calendar' },
  { path: '/contacts',   icon: '👤', label: 'Contacts' },
];

const STANDARD_WORKSPACE_PATHS = new Set([
  '/',
  '/leads',
  '/contacts',
  '/estimates',
  '/tasks',
  '/financials',
  '/commissions',
  '/leaderboard',
  '/team',
  '/employees',
  '/settings',
  '/sales-manager',
  '/team-activity',
]);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const isStandardWorkspacePage = STANDARD_WORKSPACE_PATHS.has(location.pathname);
  const isBoardWorkspacePage = location.pathname === '/pipeline' || location.pathname === '/hiring-pipeline';
  const isCalendarWorkspacePage = location.pathname === '/calendar';
  const workspaceClassName = [
    'crm-workspace',
    isStandardWorkspacePage ? 'crm-workspace--standard' : '',
    isBoardWorkspacePage ? 'crm-workspace--board' : '',
    isCalendarWorkspacePage ? 'crm-workspace--calendar' : '',
  ].filter(Boolean).join(' ');

  const handleLogout = () => { logout(); navigate('/login'); };
  
  // Determine which nav to show based on path and role
  const isGovSection = location.pathname.startsWith('/gov');
  const canAccessGov = user?.role === 'admin' || user?.role === 'contracting_officer';
  
  let NAV_ITEMS: typeof ADMIN_NAV;
  if (user?.role === 'contracting_officer') {
    NAV_ITEMS = CO_NAV;
  } else if (isGovSection && canAccessGov) {
    NAV_ITEMS = GOV_NAV;
  } else if (user?.role === 'specialist') {
    NAV_ITEMS = SPECIALIST_NAV;
  } else if (user?.role === 'pm') {
    NAV_ITEMS = PM_NAV;
  } else if (user?.role === 'admin') {
    NAV_ITEMS = ADMIN_NAV;
  } else {
    NAV_ITEMS = [];
  }

  const employeesIndex = NAV_ITEMS.findIndex(item => item.path === '/employees');
  const docsIndex = employeesIndex >= 0 ? employeesIndex + 1 : NAV_ITEMS.length;
  NAV_ITEMS = [...NAV_ITEMS.slice(0, docsIndex), { path: '/company-docs', icon: '🗂️', label: 'Company Docs' }, ...NAV_ITEMS.slice(docsIndex)];

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Sidebar */}
      <aside style={{
        width: collapsed ? 64 : 220,
        backgroundColor: '#1A2B3C',
        borderRight: '1px solid #2D4259',
        display: 'flex', flexDirection: 'column',
        transition: 'width 0.2s',
        flexShrink: 0,
      }}>
        {/* Logo */}
        <div style={{ padding: '16px', borderBottom: '1px solid #2D4259', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start' }}>
          {collapsed
            ? <img src="/emblem.png" alt="MB" style={{ height: 32, width: 32, objectFit: 'contain' }} />
            : <img src="/logo-dark.png" alt="McMullen Browne" style={{ height: 36, objectFit: 'contain' }} />
          }
        </div>

        {/* Only admins can switch between residential and government workspaces. */}
        {user?.role === 'admin' && !collapsed && (
          <div style={{ padding: '12px 8px', borderBottom: '1px solid #2D4259', display: 'flex', justifyContent: 'center' }}>
            <DivisionToggle />
          </div>
        )}

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 8px', overflow: 'auto' }}>
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={!!item.exact}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 10, marginBottom: 4,
                background: isActive ? '#4A7EB5' : 'transparent',
                color: isActive ? '#fff' : '#8FA8C0',
                fontWeight: isActive ? 700 : 400,
                fontSize: 14, transition: 'all 0.15s',
                whiteSpace: 'nowrap', overflow: 'hidden',
              })}
            >
              <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
              {!collapsed && item.label}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div style={{ padding: '12px 8px', borderTop: '1px solid #2D4259' }}>
          {!collapsed && (
            <div style={{ padding: '8px 12px', marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{user?.name}</div>
              <div style={{ fontSize: 11, color: '#4A7EB5', textTransform: 'uppercase', letterSpacing: 1 }}>{user?.role}</div>
            </div>
          )}
          <button
            onClick={handleLogout}
            style={{
              width: '100%', padding: '8px 12px', borderRadius: 10,
              background: 'transparent', color: '#4A6580',
              fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <span>🚪</span>{!collapsed && 'Sign Out'}
          </button>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          style={{
            position: 'absolute', bottom: 20, left: collapsed ? 40 : 196,
            width: 24, height: 24, borderRadius: 12,
            background: '#2D4259', color: '#8FA8C0', fontSize: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'left 0.2s',
          }}
        >
          {collapsed ? '›' : '‹'}
        </button>
      </aside>

      {/* Main content */}
      <main className={workspaceClassName} style={{ flex: 1, overflow: 'auto', background: '#0F1923' }}>
        <Outlet />
      </main>
    </div>
  );
}
